/**
 * @fileOverview Date format configuration utility module
 * @description Provides custom date format definitions for Angular Material Datepicker component.
 * Centralizes date parsing and display format specifications for consistent date handling
 * across the application's UI components.
 * 
 * @module date-format
 */

// custom-date-formats.ts
import { MatDateFormats } from '@angular/material/core';

/**
 * @const {MatDateFormats} CUSTOM_DATE_FORMATS
 * @description Custom date format configuration object for Angular Material Datepicker.
 * Defines both parsing rules for user input and display formats for the UI.
 * 
 * Formats used:
 * - DD: Day of month (01-31)
 * - MMM: 3-letter month abbreviation (Jan, Feb, etc.)
 * - YYYY: 4-digit year
 * - LL: Localized date format with locale translation
 * - MMMM: Full month name
 * 
 * @type {MatDateFormats}
 * @property {Object} parse - Input parsing configuration
 * @property {string} parse.dateInput='DD MMM YYYY' - Expected format for parsing user date input
 * @property {Object} display - Output display configuration  
 * @property {string} display.dateInput='DD MMM YYYY' - Format for date input field display
 * @property {string} display.monthYearLabel='MMM YYYY' - Format for month/year header in datepicker calendar
 * @property {string} display.dateA11yLabel='LL' - Accessibility label format for date input (uses locale)
 * @property {string} display.monthYearA11yLabel='MMMM YYYY' - Accessibility label format for month/year header
 * 
 * @example
 * // Usage in component
 * import { CUSTOM_DATE_FORMATS } from '@app/Utils/date-format';
 * import { MAT_DATE_FORMATS } from '@angular/material/core';
 * 
 * @Component({
 *   providers: [
 *     { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
 *   ]
 * })
 * export class MyDatepickerComponent { }
 */
export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  /** 
   * @property {Object} parse
   * @description Input parsing configuration that defines how user-entered date strings are parsed.
   */
  parse: {
    /** 
     * @type {string}
     * @description Expected input format for parsing dates from user input fields.
     * Format: 'DD MMM YYYY' (e.g., '19 Jan 2026')
     */
    dateInput: 'DD MMM YYYY',
  },
  /** 
   * @property {Object} display
   * @description Output display configuration that defines how dates are presented in various UI contexts.
   */
  display: {
    /** 
     * @type {string}
     * @description Format for displaying dates in the date input field.
     * Format: 'DD MMM YYYY' (e.g., '19 Jan 2026')
     */
    dateInput: 'DD MMM YYYY',
    /** 
     * @type {string}
     * @description Format for the month/year label shown in the datepicker calendar header.
     * Format: 'MMM YYYY' (e.g., 'Jan 2026')
     * Helps users navigate between months and years in the calendar view.
     */
    monthYearLabel: 'MMM YYYY',
    /** 
     * @type {string}
     * @description Accessibility label format for the date input field.
     * Uses 'LL' for locale-specific date representation for screen readers.
     * Improves accessibility for users with visual impairments.
     */
    dateA11yLabel: 'LL',
    /** 
     * @type {string}
     * @description Accessibility label format for the month/year label in the calendar header.
     * Format: 'MMMM YYYY' (e.g., 'January 2026')
     * Provides full month names for better accessibility via screen readers.
     */
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
